import React from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {
  FaChartLine,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaUserCog,
  FaLock,
  FaEnvelopeOpenText,
} from "react-icons/fa";
import "../../styles/theme.css";
import dashboardImage from "../../assets/images/Analytics_Dashboard.webp";
import { fadeInUp, staggerItem, getInitialAnimationProps, getInitialStaggerProps } from "../../utils/animations";

const analyticsSections = [
  {
    title: "Revenue Performance",
    icon: <FaChartLine />,
    desc: "Track charges, payments, and collection ratios to monitor financial health.",
    points: [
      "Total Charges & Payments",
      "Net Collections",
      "Collection Ratio",
      "Monthly Revenue Trends",
    ],
  },
  {
    title: "Accounts Receivable (A/R)",
    icon: <FaFileInvoiceDollar />,
    desc: "Analyze outstanding balances and follow-ups for faster collections.",
    points: [
      "A/R Aging Buckets",
      "Outstanding Balances",
      "Insurance vs Patient A/R",
      "Pending Follow-ups",
    ],
  },
  {
    title: "Claims & Denials",
    icon: <FaExclamationTriangle />,
    desc: "Identify denial trends and root causes to improve acceptance rates.",
    points: [
      "First-pass Acceptance Rate",
      "Denial Trends",
      "Root Cause Analysis",
      "Appeal Success Rate",
    ],
  },
  {
    title: "Operational Productivity",
    icon: <FaUserCog />,
    desc: "Measure workflow efficiency and team productivity.",
    points: [
      "Claims Processed per Day",
      "Payment Posting Turnaround",
      "Follow-up Productivity",
      "Workflow Efficiency",
    ],
  },
];

const infoBlocks = [
  {
    title: "Delivery & Frequency",
    icon: <FaEnvelopeOpenText />,
    points: [
      "Weekly performance snapshots",
      "Monthly financial summary reports",
      "On-demand reports upon request",
      "Secure email or dashboard access",
    ],
  },
  {
    title: "Security & Compliance",
    icon: <FaLock />,
    points: [
      "HIPAA-compliant reporting workflows",
      "Role-based data access",
      "Encrypted data handling",
      "Secure storage & monitoring",
    ],
  },
];

function Analytics() {
  return (
    <>
      <Helmet>
        <title>Reporting & Revenue Analytics | OptiBill Analytics</title>
        <meta name="description" content="Actionable medical billing insights: revenue performance tracking, A/R management, denial analytics, productivity dashboards, and customized reports for healthcare practices." />
        <meta name="keywords" content="medical billing analytics, revenue analytics, A/R reporting, denial management reports, billing dashboards, healthcare financial reports, RCM analytics" />
        <meta property="og:title" content="Reporting & Revenue Analytics | OptiBill Analytics" />
        <meta property="og:description" content="Actionable insights into claims, payments, denials, and A/R—designed to help healthcare practices make smarter financial decisions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.optibillanalytics.com/analytics" />
        <link rel="canonical" href="https://www.optibillanalytics.com/analytics" />
      </Helmet>
      <section className="page-shell">
        <div className="page-container">
          {/* Page Header */}
          <motion.div className="page-header" {...getInitialAnimationProps(fadeInUp, 0.2)}>
            <h1 className="page-title">
              Reporting & Revenue Analytics
            </h1>
            <div className="section-divider"></div>
            <p className="page-subtitle">
              Actionable insights into claims, payments, denials, and A/R—designed to help healthcare practices make smarter financial decisions.
            </p>
          </motion.div>

          {/* Analytics Grid */}
          <motion.div 
            className="row g-4"
            {...getInitialStaggerProps(0.4)}
          >
            {analyticsSections.map((section, index) => (
              <motion.div 
                className="col-md-6" 
                key={index}
                variants={staggerItem}
              >
                <div className="service-card h-100 text-center p-4">
                  <div className="service-icon mb-3" style={{ fontSize: '2.2rem' }}>
                    {section.icon}
                  </div>
                  <h5 className="fw-semibold">{section.title}</h5>
                  <p className="text-muted small mt-2">{section.desc}</p>
                  <ul className="list-clean">
                    {section.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Dashboard Preview */}
          <div className="row mt-5 mb-5 align-items-center">
            <div className="col-md-6">
              <div style={{ textAlign: 'center' }}>
                <h4 className="fw-bold text-navy" style={{ fontSize: '1.35rem', marginBottom: '0.7rem' }}>
                  Visual Dashboards & Reports
                </h4>
                <div className="section-divider" style={{ width: 72, marginBottom: '1.2rem' }}></div>
              </div>
              <p style={{ color: '#5A6478', fontSize: '1.08rem', marginBottom: '0.7rem' }}>
                Our dashboards provide a clear view of revenue trends, A/R status, and denial patterns—helping you identify issues early and take corrective action.
              </p>
              <p className="small text-muted" style={{ color: '#5A6478' }}>
                *Dashboards shown are illustrative and customized per client.
              </p>
            </div>
            <div className="col-md-6">
              <img 
                src={dashboardImage} 
                alt="Analytics Dashboard Preview" 
                style={{ width: '100%', height: 'auto', borderRadius: 20, boxShadow: '0 18px 36px rgba(16, 35, 62, 0.16)', border: '1px solid rgba(16,35,62,0.12)' }}
              />
            </div>
          </div>

          {/* Info Blocks */}
          <div className="row g-4 mb-5">
            {infoBlocks.map((block, idx) => (
              <div className="col-md-6" key={idx}>
                <div className="service-card h-100 text-center p-4">
                  <div className="service-icon mb-3" style={{ fontSize: '2.2rem' }}>
                    {block.icon}
                  </div>
                  <h5 className="fw-semibold">{block.title}</h5>
                  <ul className="list-clean">
                    {block.points.map((point, i) => (
                      <li key={i}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="cta-panel mt-5">
            <h2>Turn Billing Data Into Revenue Intelligence</h2>
            <p>
              Get a free revenue analysis tailored to your practice.
            </p>
            <button className="about-btn"
              onClick={() => window.openContactPopup && window.openContactPopup()}>
              Request Free Revenue Analysis
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

export default Analytics;
